import React, { useMemo, useState, useCallback } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, parseISO, isValid } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
//@ts-ignore
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {
  Box,
  Typography,
  Paper,
  Drawer,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Divider,
  Button,
  Stack,
  GlobalStyles,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
} from '@mui/icons-material';
import { useBirdBatches } from '../../hooks/useBirds';
import { useBatchSchedule, useUpcomingTasks, useStartEvent } from '../../hooks/usePreventiveHealth';

// ── date-fns localizer ─────────────────────────────────────────────────────────

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales: { 'en-US': enUS },
});

// ── Constants ──────────────────────────────────────────────────────────────────

const TRACK_COLORS: Record<string, string> = {
  vaccination: '#42a5f5',
  antiparasitic: '#ff9800',
  vitamin: '#66bb6a',
  biosecurity: '#ab47bc',
  monitoring: '#ef5350',
};

const TRACK_LABELS: Record<string, string> = {
  vaccination: 'Vaccination',
  antiparasitic: 'Antiparasitic',
  vitamin: 'Vitamin',
  biosecurity: 'Biosecurity',
  monitoring: 'Monitoring',
};

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    color: 'success' | 'warning' | 'error' | 'info' | 'default';
    icon: React.ReactNode;
  }
> = {
  completed: { label: 'Completed', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  scheduled: { label: 'Scheduled', color: 'info', icon: <ScheduleIcon fontSize="small" /> },
  overdue: { label: 'Overdue', color: 'error', icon: <WarningIcon fontSize="small" /> },
  in_progress: { label: 'In Progress', color: 'warning', icon: <PlayArrowIcon fontSize="small" /> },
  missed: { label: 'Missed', color: 'default', icon: <CancelIcon fontSize="small" /> },
};

// ── Dark-mode CSS overrides for react-big-calendar ────────────────────────────

const calendarStyles = (
  <GlobalStyles
    styles={{
      '.rbc-calendar': {
        background: '#141414',
        color: '#fff',
        fontFamily: 'Inter, -apple-system, sans-serif',
      },
      '.rbc-header': {
        background: '#1a1a1a',
        borderColor: 'rgba(255,152,0,0.15)',
        color: 'rgba(255,255,255,0.7)',
        fontWeight: 600,
        fontSize: '0.75rem',
        letterSpacing: '0.5px',
        padding: '8px 4px',
      },
      '.rbc-month-view': {
        borderColor: 'rgba(255,152,0,0.15)',
      },
      '.rbc-month-row': {
        borderColor: 'rgba(255,152,0,0.1)',
      },
      '.rbc-day-bg': {
        background: '#141414',
        borderColor: 'rgba(255,152,0,0.1)',
      },
      '.rbc-day-bg + .rbc-day-bg': {
        borderColor: 'rgba(255,152,0,0.1)',
      },
      '.rbc-off-range-bg': {
        background: '#0f0f0f',
      },
      '.rbc-today': {
        background: 'rgba(255,152,0,0.08)',
      },
      '.rbc-date-cell': {
        color: 'rgba(255,255,255,0.6)',
        fontSize: '0.8rem',
        padding: '4px 6px',
      },
      '.rbc-date-cell.rbc-now': {
        color: '#ff9800',
        fontWeight: 700,
      },
      '.rbc-date-cell.rbc-off-range': {
        color: 'rgba(255,255,255,0.25)',
      },
      '.rbc-event': {
        borderRadius: '4px',
        fontSize: '0.72rem',
        fontWeight: 500,
        padding: '2px 5px',
        border: 'none',
        cursor: 'pointer',
      },
      '.rbc-event.rbc-selected': {
        outline: '2px solid #ff9800',
      },
      '.rbc-event-label': {
        display: 'none',
      },
      '.rbc-show-more': {
        color: '#ff9800',
        background: 'transparent',
        fontWeight: 600,
        fontSize: '0.72rem',
      },
      '.rbc-toolbar': {
        marginBottom: '12px',
        flexWrap: 'wrap',
        gap: '8px',
      },
      '.rbc-toolbar button': {
        color: 'rgba(255,255,255,0.7)',
        background: '#1a1a1a',
        border: '1px solid rgba(255,152,0,0.2)',
        borderRadius: '8px',
        padding: '6px 12px',
        fontSize: '0.85rem',
        cursor: 'pointer',
        transition: 'all 0.15s',
      },
      '.rbc-toolbar button:hover': {
        background: 'rgba(255,152,0,0.12)',
        color: '#ff9800',
        borderColor: '#ff9800',
      },
      '.rbc-toolbar button.rbc-active': {
        background: 'rgba(255,152,0,0.2)',
        color: '#ff9800',
        borderColor: '#ff9800',
      },
      '.rbc-toolbar-label': {
        color: '#fff',
        fontWeight: 700,
        fontSize: '1rem',
      },
      '.rbc-row-segment': {
        padding: '1px 2px',
      },
    }}
  />
);

// ── Types ──────────────────────────────────────────────────────────────────────

interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  resource: any;
}

// ── Component ──────────────────────────────────────────────────────────────────

const PreventiveCalendar: React.FC = () => {
  const [batchId, setBatchId] = useState<number | ''>('');
  const [trackFilter, setTrackFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [view, setView] = useState<View>('month');
  const [date, setDate] = useState(new Date());

  const startEvent = useStartEvent();

  // Batch list for the filter dropdown
  const { data: batchesData } = useBirdBatches({ per_page: 100, status: 'active' });
  const batches = batchesData?.data ?? [];
  const scheduleParams = {
    per_page: 2000,
    ...(trackFilter && { track: trackFilter }),
    ...(statusFilter && { status: statusFilter }),
  };

  // When a batch is selected: fetch its full schedule (past + future)
  const { data: scheduleData, isLoading: scheduleLoading } = useBatchSchedule(
    batchId || undefined,
    scheduleParams
  );

  // When no batch selected: fetch upcoming tasks (next 90 days)
  const upcomingParams = {
    days: 90,
    per_page: 200,
    ...(trackFilter && { track: trackFilter }),
  };
  const { data: upcomingData, isLoading: upcomingLoading } = useUpcomingTasks(
    batchId ? undefined : upcomingParams
  );

  const isLoading = batchId ? scheduleLoading : upcomingLoading;

  // Map API events → react-big-calendar event objects
  const calendarEvents: CalendarEvent[] = useMemo(() => {
    const source = batchId ? scheduleData : upcomingData;
    // Both endpoints return paginated {data: [...], meta: {...}} — unwrap if needed
    const raw: any[] = Array.isArray(source) ? source : (source?.data ?? []);
    return raw
      .filter((e) => {
        if (!e.scheduled_date) return false;
        const d = parseISO(e.scheduled_date);
        return isValid(d);
      })
      .map((e) => {
        const d = parseISO(e.scheduled_date);
        const batchLabel = e.batch?.identifier ?? '';
        const eventName = e.event?.name ?? e.event_name ?? 'Event';
        return {
          id: e.id,
          title: batchId ? eventName : `${batchLabel}: ${eventName}`,
          start: d,
          end: d,
          resource: e,
        };
      });
  }, [batchId, scheduleData, upcomingData]);

  // Colour each event by its track
  const eventPropGetter = useCallback((event: CalendarEvent) => {
    const track = event.resource?.event?.track ?? event.resource?.track ?? '';
    const color = TRACK_COLORS[track] ?? '#9e9e9e';
    const status = event.resource?.status ?? 'scheduled';
    const opacity = status === 'completed' ? 0.45 : status === 'missed' ? 0.35 : 1;
    return {
      style: {
        backgroundColor: color,
        opacity,
        border: 'none',
        color: '#fff',
      },
    };
  }, []);

  const handleSelectEvent = useCallback((event: CalendarEvent) => {
    setSelectedEvent(event.resource);
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedEvent(null);
  };

  const handleStartEvent = () => {
    if (!selectedEvent) return;
    startEvent.mutate(selectedEvent.id, {
      onSuccess: (updated) => {
        setSelectedEvent((prev: any) => ({ ...prev, status: 'in_progress', ...updated }));
      },
    });
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const statusCfg = selectedEvent
    ? (STATUS_CONFIG[selectedEvent.status] ?? STATUS_CONFIG.scheduled)
    : null;
  const eventTrack = selectedEvent?.event?.track ?? '';
  const trackColor = TRACK_COLORS[eventTrack] ?? '#9e9e9e';

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {calendarStyles}

      {/* Page header */}
      <Box display="flex" alignItems="center" gap={1.5} mb={3}>
        <CalendarIcon sx={{ color: '#ff9800', fontSize: 28 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Preventive Health Calendar
          </Typography>
          <Typography variant="caption" color="text.secondary">
            All scheduled events across your flocks
          </Typography>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        sx={{ p: 2, mb: 2.5, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}
      >
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>All batches</InputLabel>
          <Select
            value={batchId}
            label="All batches"
            onChange={(e) => setBatchId(e.target.value as number | '')}
          >
            <MenuItem value="">All batches</MenuItem>
            {batches.map((b: any) => (
              <MenuItem key={b.id} value={b.id}>
                {b.identifier}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>All tracks</InputLabel>
          <Select
            value={trackFilter}
            label="All tracks"
            onChange={(e) => setTrackFilter(e.target.value)}
          >
            <MenuItem value="">All tracks</MenuItem>
            {Object.entries(TRACK_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      bgcolor: TRACK_COLORS[value],
                    }}
                  />
                  {label}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>All statuses</InputLabel>
          <Select
            value={statusFilter}
            label="All statuses"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="scheduled">Scheduled</MenuItem>
            <MenuItem value="in_progress">In Progress</MenuItem>
            <MenuItem value="overdue">Overdue</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="missed">Missed</MenuItem>
          </Select>
        </FormControl>

        {isLoading && <CircularProgress size={20} sx={{ ml: 'auto' }} />}

        {/* Track legend */}
        <Box display="flex" gap={1} flexWrap="wrap" ml="auto">
          {Object.entries(TRACK_LABELS).map(([track, label]) => (
            <Box
              key={track}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                bgcolor: `${TRACK_COLORS[track]}22`,
                border: `1px solid ${TRACK_COLORS[track]}55`,
              }}
            >
              <Box
                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: TRACK_COLORS[track] }}
              />
              <Typography variant="caption" sx={{ color: TRACK_COLORS[track], fontWeight: 600 }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Calendar */}
      <Paper sx={{ p: 2, height: 680 }}>
        {!batchId && !isLoading && calendarEvents.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            height="100%"
            gap={2}
          >
            <CalendarIcon sx={{ fontSize: 64, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary">
              No upcoming events
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Select a batch or check that schedules have been generated.
            </Typography>
          </Box>
        ) : (
          <Calendar
            localizer={localizer}
            events={calendarEvents}
            view={view}
            date={date}
            onView={(v) => setView(v)}
            onNavigate={(d) => setDate(d)}
            eventPropGetter={eventPropGetter as any}
            onSelectEvent={handleSelectEvent}
            style={{ height: '100%' }}
            popup
            showAllEvents={false}
          />
        )}
      </Paper>

      {/* Event detail drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: 420,
            bgcolor: '#141414',
            borderLeft: '1px solid rgba(255,152,0,0.2)',
            p: 3,
            overflowY: 'auto',
          },
        }}
      >
        {selectedEvent && (
          <Box>
            {/* Drawer header */}
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
              <Box flex={1} pr={1}>
                <Box
                  sx={{
                    display: 'inline-block',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: `${trackColor}22`,
                    border: `1px solid ${trackColor}55`,
                    mb: 1,
                  }}
                >
                  <Typography variant="caption" fontWeight={700} sx={{ color: trackColor }}>
                    {TRACK_LABELS[eventTrack] ?? eventTrack}
                  </Typography>
                </Box>
                <Typography variant="h6" fontWeight={700} lineHeight={1.3}>
                  {selectedEvent.event?.name ?? selectedEvent.event_name}
                </Typography>
              </Box>
              <IconButton size="small" onClick={handleCloseDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Status chip */}
            {statusCfg && (
              <Chip
                icon={statusCfg.icon as any}
                label={statusCfg.label}
                color={statusCfg.color}
                size="small"
                sx={{ mb: 2, fontWeight: 600 }}
              />
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Batch & date info */}
            <Stack spacing={1.5} mb={2}>
              <InfoRow label="Batch" value={selectedEvent.batch?.identifier ?? '—'} />
              <InfoRow label="Scheduled Date" value={selectedEvent.scheduled_date ?? '—'} />
              {selectedEvent.egg_withdrawal_until && (
                <InfoRow
                  label="Egg Withdrawal Until"
                  value={selectedEvent.egg_withdrawal_until}
                  valueColor="warning.main"
                />
              )}
              <InfoRow label="Assigned To" value={selectedEvent.assignedTo?.name ?? 'Unassigned'} />
            </Stack>

            <Divider sx={{ mb: 2 }} />

            {/* Product & dose */}
            {selectedEvent.event && (
              <Stack spacing={1.5} mb={2}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                >
                  Treatment Details
                </Typography>
                {selectedEvent.event.product && (
                  <InfoRow label="Product" value={selectedEvent.event.product.name} />
                )}
                {selectedEvent.event.dose && (
                  <InfoRow label="Dose" value={selectedEvent.event.dose} />
                )}
                {selectedEvent.event.route && (
                  <InfoRow label="Route" value={selectedEvent.event.route} />
                )}
                {selectedEvent.event.instructions && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Instructions
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.5, lineHeight: 1.6, color: 'text.secondary' }}
                    >
                      {selectedEvent.event.instructions}
                    </Typography>
                  </Box>
                )}
              </Stack>
            )}

            {/* Completion record */}
            {selectedEvent.completion && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={1.5} mb={2}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
                  >
                    Completion Record
                  </Typography>
                  <InfoRow
                    label="Completed By"
                    value={selectedEvent.completion.completedBy?.name ?? '—'}
                  />
                  <InfoRow
                    label="Completed At"
                    value={selectedEvent.completion.completed_at ?? '—'}
                  />
                  {selectedEvent.completion.birds_treated != null && (
                    <InfoRow
                      label="Birds Treated"
                      value={String(selectedEvent.completion.birds_treated)}
                    />
                  )}
                  {selectedEvent.completion.actual_dose && (
                    <InfoRow label="Actual Dose" value={selectedEvent.completion.actual_dose} />
                  )}
                  {selectedEvent.completion.product_used && (
                    <InfoRow
                      label="Product Used"
                      value={selectedEvent.completion.product_used.name}
                    />
                  )}
                  {selectedEvent.completion.observations && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Observations
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, lineHeight: 1.6, color: 'text.secondary' }}
                      >
                        {selectedEvent.completion.observations}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </>
            )}

            {/* Actions */}
            {['scheduled', 'overdue'].includes(selectedEvent.status) && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartEvent}
                  disabled={startEvent.isPending}
                  sx={{ color: '#fff' }}
                >
                  {startEvent.isPending ? 'Starting…' : 'Mark as In Progress'}
                </Button>
              </>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

// ── Small helper ───────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: string; valueColor?: string }> = ({
  label,
  value,
  valueColor,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} color={valueColor ?? 'text.primary'}>
      {value}
    </Typography>
  </Box>
);

export default PreventiveCalendar;
